<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { t } from './i18n';
import { buildArcSvgFragment, measureArcTextLayout } from '../../../src/shared/arcText/ArcLayout';

interface Item {
  id?: string;
  type?: string;
  text?: unknown;
  color?: unknown;
  textCurve?: unknown;
  textCurvePreview?: unknown;
  [key: string]: unknown;
}

const props = defineProps<{
  item: Item | null;
  update: (patch: Record<string, unknown>) => void;
}>();

const ZERO_SNAP = 5;

const CURVE_PRESETS = [
  { label: 'Straight', value: 0, d: 'M3 12h18' },
  { label: 'Arch', value: 60, d: 'M3 16Q12 4 21 16' },
  { label: 'Arch down', value: -60, d: 'M3 8Q12 20 21 8' },
  { label: 'Full', value: 100, d: 'M12 4a8 8 0 1 1 0 16a8 8 0 1 1 0-16' },
] as const;

const isText = computed(() => props.item?.type === 'text');
const isInteracting = ref(false);
const curveAmount = computed(() => clampCurve(Number(props.item?.textCurve ?? 0)));
const curveSpacing = computed(() => Math.max(-20, Math.min(100, Number(props.item?.textCurveSpacing ?? 0))));
const curveReverse = computed(() => Boolean(props.item?.textCurveReverse));
const signedCurveLabel = computed(() => (curveAmount.value > 0 ? `+${curveAmount.value}` : String(curveAmount.value)));
const isDefault = computed(() => curveAmount.value === 0 && curveSpacing.value === 0 && !curveReverse.value);

const previewText = computed(() => {
  const raw = String(props.item?.text ?? '').replace(/\s+/g, ' ').trim();
  return raw || t('Preview');
});

/** Inline SVG (or plain text) preview of the current curve, scaled to fit. */
const previewSvg = computed(() => {
  if (!isText.value) return '';
  const previewItem = {
    ...(props.item as Record<string, unknown>),
    text: previewText.value,
    color: 'currentColor',
    opacity: 1,
    textCurve: curveAmount.value,
    textCurvePreview: false,
  };
  const layout = measureArcTextLayout(previewItem as never);
  if (!layout) return '';
  const fragment = buildArcSvgFragment(previewItem as never);
  return `<svg viewBox="0 0 ${layout.width} ${layout.height}" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style="overflow:visible">${fragment}</svg>`;
});

function clampCurve(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(-100, Math.min(100, Math.round(value)));
}

function setCurve(value: number) {
  props.update({ textCurve: clampCurve(value) });
}

function setSpacing(value: number) {
  props.update({ textCurveSpacing: Math.max(-20, Math.min(100, Math.round(value))) });
}

function setReverse(value: boolean) {
  props.update({ textCurveReverse: value });
}

function beginPreview() {
  if (!isText.value) return;
  isInteracting.value = true;
  props.update({ textCurvePreview: true });
}

function endPreview() {
  if (!isInteracting.value) return;
  isInteracting.value = false;
  props.update({ textCurvePreview: false });
}

/** Canva-style detent: settle to 0 when released near the center. */
function snapToZero() {
  if (curveAmount.value !== 0 && Math.abs(curveAmount.value) <= ZERO_SNAP) {
    setCurve(0);
  }
}

function applyPreset(value: number) {
  if (!isText.value) return;
  setCurve(value);
}

function handleSliderInput(event: Event) {
  beginPreview();
  setCurve(Number((event.target as HTMLInputElement).value));
}

function handleSliderRelease() {
  snapToZero();
  endPreview();
}

function handleNumberInput(event: Event) {
  beginPreview();
  setCurve(Number((event.target as HTMLInputElement).value.replace(/^\+/, '')));
}

/** Arrow keys nudge the value (Shift = ×10) without leaving the field. */
function handleNumberKeydown(event: KeyboardEvent) {
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
  event.preventDefault();
  beginPreview();
  setCurve(curveAmount.value + (event.key === 'ArrowUp' ? 1 : -1) * (event.shiftKey ? 10 : 1));
}

function resetCurve() {
  props.update({ textCurve: 0, textCurveSpacing: 0, textCurveReverse: false });
}

onBeforeUnmount(endPreview);
</script>

<template>
  <section class="vptw:flex vptw:flex-col vptw:gap-4">
    <div
      v-if="!isText"
      class="vptw:rounded-xl vptw:border vptw:border-dashed vptw:border-(--vs-line) vptw:bg-(--vs-surface-2) vptw:px-4 vptw:py-5 vptw:text-center"
    >
      <p class="vptw:mb-1 vptw:text-sm vptw:font-medium vptw:text-(--vs-ink)">{{ t('Select a text layer') }}</p>
      <p class="vptw:mb-0 vptw:text-xs vptw:leading-5 vptw:text-(--vs-ink-2)">
        {{ t('Choose text on the design canvas to curve it along an arc.') }}
      </p>
    </div>

    <template v-else>
      <!-- Live preview -->
      <div class="vptw:relative vptw:flex vptw:h-24 vptw:items-center vptw:justify-center vptw:overflow-hidden vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-(--vs-surface-2) vptw:px-4 vptw:text-(--vs-ink)">
        <div v-if="previewSvg" class="vptw:flex vptw:h-16 vptw:w-full vptw:items-center vptw:justify-center" v-html="previewSvg" />
        <span v-else class="vptw:truncate vptw:text-xl vptw:font-medium">{{ previewText }}</span>
        <span
          v-if="curveAmount !== 0"
          class="vptw:absolute vptw:right-2 vptw:top-2 vptw:rounded-full vptw:bg-(--vs-accent-soft) vptw:px-2 vptw:py-0.5 vptw:text-[10px] vptw:font-semibold vptw:tabular-nums vptw:text-(--vs-accent)"
        >
          {{ signedCurveLabel }}
        </span>
      </div>

      <!-- Shape presets -->
      <div class="vptw:grid vptw:grid-cols-4 vptw:gap-1.5">
        <button
          v-for="preset in CURVE_PRESETS"
          :key="preset.label"
          type="button"
          :aria-pressed="curveAmount === preset.value"
          class="vptw:flex! vptw:flex-col! vptw:items-center! vptw:justify-start! vptw:gap-1! vptw:rounded-xl! vptw:border! vptw:px-1! vptw:py-2! vptw:text-[10px]! vptw:font-medium! vptw:leading-tight! vptw:transition vptw:duration-150 vptw:cursor-pointer vptw:outline-none! vptw:focus-visible:ring-2! vptw:focus-visible:ring-(--vs-accent)!"
          :class="curveAmount === preset.value
            ? 'vptw:border-(--vs-accent)! vptw:bg-(--vs-accent-soft)! vptw:text-(--vs-accent)!'
            : 'vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:text-(--vs-ink-2)! vptw:hover:bg-(--vs-surface-2)! vptw:hover:text-(--vs-ink)!'"
          @click="applyPreset(preset.value)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="vptw:size-5"><path :d="preset.d" /></svg>
          <span class="vptw:max-w-full vptw:text-center vptw:break-words">{{ t(preset.label) }}</span>
        </button>
      </div>

      <!-- Curve amount -->
      <div>
        <div class="vptw:mb-1.5 vptw:flex vptw:items-center vptw:justify-between">
          <label class="vptw:text-[11px] vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Curve') }}</label>
          <input
            type="text"
            inputmode="numeric"
            :aria-label="t('Curve')"
            :value="signedCurveLabel"
            class="vptw:w-14! vptw:rounded-lg! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:px-1.5! vptw:py-1! vptw:text-center! vptw:text-[11px]! vptw:font-medium! vptw:tabular-nums vptw:text-(--vs-ink)! vptw:outline-none! vptw:transition vptw:focus:border-(--vs-accent)!"
            @focus="($event.target as HTMLInputElement).select(); beginPreview()"
            @input="handleNumberInput"
            @keydown="handleNumberKeydown"
            @keydown.enter="($event.target as HTMLInputElement).blur()"
            @change="endPreview"
            @blur="endPreview"
          />
        </div>
        <input
          type="range"
          min="-100"
          max="100"
          step="1"
          :aria-label="t('Curve')"
          :value="curveAmount"
          class="vptw:w-full"
          @pointerdown="beginPreview"
          @pointerup="handleSliderRelease"
          @pointercancel="handleSliderRelease"
          @input="handleSliderInput"
          @change="handleSliderRelease"
        />
        <div class="vptw:mt-0.5 vptw:flex vptw:justify-between vptw:text-[10px] vptw:text-(--vs-ink-3)">
          <span>{{ t('Down') }}</span>
          <span>0</span>
          <span>{{ t('Up') }}</span>
        </div>
      </div>

      <!-- Character spacing -->
      <div>
        <label class="vptw:mb-1.5 vptw:flex vptw:items-center vptw:justify-between vptw:text-[11px] vptw:font-medium vptw:text-(--vs-ink-2)">
          <span>{{ t('Character spacing') }}</span>
          <span class="vptw:tabular-nums">{{ curveSpacing }}</span>
        </label>
        <input
          type="range"
          min="-20"
          max="100"
          step="1"
          :aria-label="t('Character spacing')"
          :value="curveSpacing"
          class="vptw:w-full"
          @input="setSpacing(Number(($event.target as HTMLInputElement).value))"
        />
      </div>

      <!-- Reverse direction -->
      <button
        type="button"
        role="switch"
        :aria-checked="curveReverse"
        class="vptw:flex! vptw:w-full! vptw:items-center! vptw:justify-between! vptw:gap-3! vptw:border-none! vptw:bg-transparent! vptw:p-0! vptw:text-left vptw:cursor-pointer vptw:outline-none! vptw:focus-visible:ring-2! vptw:focus-visible:ring-(--vs-accent)! vptw:rounded-md!"
        @click="setReverse(!curveReverse)"
      >
        <span class="vptw:text-[11px] vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Reverse path direction') }}</span>
        <span
          class="vptw:relative vptw:inline-flex vptw:h-5 vptw:w-9 vptw:shrink-0 vptw:rounded-full vptw:transition-colors vptw:duration-200"
          :class="curveReverse ? 'vptw:bg-(--vs-accent)' : 'vptw:bg-(--vs-line)'"
        >
          <span
            class="vptw:absolute vptw:left-0.5 vptw:top-0.5 vptw:size-4 vptw:rounded-full vptw:bg-white vptw:shadow-sm vptw:transition-transform vptw:duration-200"
            :class="curveReverse ? 'vptw:translate-x-4' : 'vptw:translate-x-0'"
          />
        </span>
      </button>

      <button
        type="button"
        :disabled="isDefault"
        class="vptw:flex! vptw:w-full! vptw:items-center! vptw:justify-center! vptw:gap-1.5! vptw:rounded-xl! vptw:border! vptw:border-(--vs-line)! vptw:bg-transparent! vptw:px-3! vptw:py-2! vptw:text-xs! vptw:font-medium! vptw:text-(--vs-ink-2)! vptw:transition vptw:cursor-pointer vptw:hover:bg-(--vs-surface-2)! vptw:hover:text-(--vs-ink)! vptw:disabled:cursor-default vptw:disabled:opacity-40 vptw:disabled:hover:bg-transparent!"
        @click="resetCurve"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="vptw:size-3.5"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
        {{ t('Reset curve') }}
      </button>
    </template>
  </section>
</template>
